import { useRouter } from "next/router";
import { getAllPostsForHome, getAllTagsWithSlug, getTagPostsForSlug } from "../../lib/api";
import { GetStaticPaths, GetStaticProps } from "next";
import Link from "next/link";
import Image from "next/image";
import Layout from "../../components/layout";
import "../../styles/scss/blog.module.scss";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import Custom404 from "../404";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../next-i18next.config";
import TagHeroSection from "../../components/v2/blog/tag-hero-section";
import { getTopicFeaturedPosts } from "../../lib/topic-featured-posts";
import CallToActionSmall from "../../components/v2/call-to-action-sm";

export default function TagArchive({
  posts,
  totalPosts,
  postPerPage,
  allPosts,
  topicTags,
}) {
  const [postArchive, setPostArchive] = useState([]);
  const router = useRouter();
  const { slug, page } = router.query;
  const pageSlug = typeof slug === "string" && slug;
  const pageNum = typeof page === "string" && page;

  const currentPage = parseInt(pageNum) || 1;
  // Avoid totalPages=0 when a tag has no posts (would force 404 on page 1).
  const totalPages = Math.max(1, Math.ceil(totalPosts / postPerPage));

  if (currentPage > totalPages || currentPage < 1) {
    return <Custom404 />;
  }

  let uri = `/topics/${pageSlug}/?page=${pageNum}`;
  let nextPagePath = `/topics/${pageSlug}/?page=${currentPage + 1}`;
  let prevPagePath = `/topics/${pageSlug}/?page=${currentPage - 1}`;

  useEffect(() => {
    let offset = postPerPage * (currentPage - 1);
    let postData = posts?.categories?.edges?.[0]?.node.posts.nodes;
    let __postData = postData?.slice(offset, offset + postPerPage);
    setPostArchive(__postData);
  }, [posts, currentPage, postPerPage]);

  const tagNode = posts?.categories?.edges?.[0]?.node;
  const tagName = tagNode?.name;
  const tagDescription = tagNode?.description;
  const tagSeoMetaDesc = tagNode?.seo?.metaDesc;
  const tagSeoTitle = tagNode?.seo?.title;
  const metaDescription =
    tagSeoMetaDesc || tagDescription || "";

  const featuredPosts = getTopicFeaturedPosts(tagNode?.slug);

  return (
    <>
      <Layout
        isHomePage={false}
        pageTitle={tagSeoTitle || tagName}
        ogImage={"https://middleware.io/wp-content/uploads/2024/08/middleware-observability-thumbnail.png"}
        uri={uri}
        metaTitle={tagSeoTitle || tagName}
        metaDesc={metaDescription}
        metaRobotsNoindex={"index"}
        metaRobotsNofollow={"follow"}
      >
        <div className="middle-wrapper site-page blog-v2 category-page tag-page">
          <TagHeroSection
            tagName={tagName}
            tagDescription={tagDescription}
            topicTags={topicTags}
            excludeSlug={tagNode?.slug}
          />

          <div className="container">
            {featuredPosts.length > 0 && (
              <div className="featured-posts-tags">
                <div className="top-heading">
                  <h2>Featured Posts</h2>
                </div>
                <div className={"below-section"}>
                  <div className={"d-flex"}>
                    {featuredPosts.map((item) => (
                      <Link
                        key={item.href}
                        href={item.href}
                        className="read-more"
                      >
                        <div className="topic-card">
                          <h3>{item.title}</h3>
                          {item.description?.trim() ? (
                            <p>{item.description}</p>
                          ) : null}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}

          <div className="top-heading"><h2>Other {tagName} Posts</h2></div>
            <div className={"below-section"}>
              <div className={"d-flex"}>
                {postArchive?.map((post, index) => {
                  let link = post.slug;
                  if (post?.parentSlug?.parentSlug != null && post?.parentSlug?.parentSlug.length > 0) {
                    link = post.parentSlug.parentSlug;
                  }
                  const categoryNodes = post?.categories?.nodes ?? [];
                  return (
                    <div className={"category-item"} key={post.slug ?? index}>
                        
                        {categoryNodes.length > 0 && (
                          <div className="category knowledge">
                          {categoryNodes.map((cat) => (
                            <Link
                              key={cat.slug}
                              href={`/blog/category/${cat.slug}/`}
                              className="topic-post-category-link"
                            >
                              {cat.name}
                            </Link>
                          ))}
                        </div>
                        )}
                        <Link href={`/blog/${link}/`}>
                          <h4>{post.title}</h4>
                        </Link>
                        <div className={"date-author"}>
                          <p>
                            <Link href={`/blog/authors/${post?.author?.node?.slug}`}>
                              {post?.author?.node?.name}
                            </Link>
                          </p>
                          <p>{dayjs(post?.date).format("MMM D, YYYY")}</p>
                        </div>
                      </div>
                  );
                })}
              </div>
            </div>
          </div>

          <section className="pagination">
            <div className="container">
              <div className="d-flex">
                <div className="pre">
                  {currentPage > 1 && (
                    <>
                      <Link href={prevPagePath}>
                        <Image
                          src={"/images/left-slide.svg"}
                          alt={"Previous tag page"}
                          width={42}
                          height={32}
                          unoptimized
                        />
                      </Link>
                      <br />
                    </>
                  )}
                </div>
                <div className="next">
                  {currentPage < totalPages && (
                    <Link href={nextPagePath}>
                      <Image
                        src={"/images/right-slide.svg"}
                        alt={"Next tag page"}
                        width={42}
                        height={32}
                        unoptimized
                      />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
        <CallToActionSmall />
      </Layout>
    </>
  );
}

export const getStaticProps: GetStaticProps = async ({ params, preview = false, locale }) => {
  const data = await getTagPostsForSlug(params?.slug as string | undefined);

  if (!data?.categories?.edges?.length) {
    return { notFound: true };
  }

  const totalPosts = data.categories.edges[0].node.posts.nodes.length;
  const tagNode = data.categories.edges[0].node;
  const [allPosts, allTagsData] = await Promise.all([
    getAllPostsForHome(preview, locale ?? "en"),
    getAllTagsWithSlug(),
  ]);
  const topicTags =
    allTagsData?.edges?.map(({ node }) => ({
      slug: node.slug,
      name: node.name,
    })) ?? [];
  const uri = `/topics/${tagNode.slug}/`;
  const defaultMeta =
    "Read how observability can scale your cloud infrastructure, best practices, how-tos guide and industry updates from experts.";
  const metaDescText =
    tagNode.seo?.metaDesc || tagNode.description || defaultMeta;
  const pageTitleText =
    tagNode.seo?.title || tagNode.name || "Observability News, and Best Practices and Guides";
  return {
    props: {
      preview,
      posts: data,
      totalPosts,
      allPosts,
      topicTags,
      postPerPage: 8,
      description: metaDescText,
      pageTitle: pageTitleText,
      ogImage: "https://middleware.io/wp-content/uploads/2024/08/middleware-observability-thumbnail.png",
      uri,
      metaTitle: pageTitleText,
      metaDesc: metaDescText,
      metaRobotsNoindex: "index",
      metaRobotsNofollow: "follow",
      ...(await serverSideTranslations(locale ?? "en", ["common", "header", "footer"], nextI18NextConfig)),
    },
  };
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const allTags = await getAllTagsWithSlug();

  let paths = [];
  allTags?.edges?.forEach((item) => {
    if (!item?.node?.slug) {
      return;
    }
    if (locales) {
      locales.forEach((locale) => {
        paths.push({
          params: { slug: item.node.slug },
          locale,
        });
      });
    } else {
      paths.push({
        params: { slug: item.node.slug },
      });
    }
  });

  return {
    paths: paths,
    fallback: "blocking",
  };
};
